import React, { useState } from 'react';
import axios from 'axios';
import API_ENDPOINTS from '../ApiEndPoints';

const WebpageLogs = ({ pageId }) => {
    const [filters, setFilters] = useState({
        dateFrom: '',
        dateTo: '',
        ipAddress: '',
        referrer: '',
        params: '',
        uid: '',
        search: ''
    });
    const [logs, setLogs] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [offset, setOffset] = useState(0);
    const [hasLoaded, setHasLoaded] = useState(false);
    const LIMIT = 20;

    const [sortColumn, setSortColumn] = useState('created_at');
    const [sortDirection, setSortDirection] = useState('desc');

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const loadLogs = async (reset = false) => {
        setLoading(true);
        try {
            const currentOffset = reset ? 0 : offset;
            const params = {
                limit: LIMIT,
                offset: currentOffset,
                date_from: filters.dateFrom,
                date_to: filters.dateTo,
                ip_address: filters.ipAddress,
                referrer: filters.referrer,
                params: filters.params,
                uid: filters.uid,
                search: filters.search,
                sort_by: sortColumn,
                sort_direction: sortDirection
            };

            // Construct URL manually if API_ENDPOINTS doesn't support params directly or use axios params
            // Assuming API_ENDPOINTS.webpages.getLogs(pageId) returns the base URL
            const response = await axios.get(API_ENDPOINTS.webpages.getLogs(pageId), { params });

            if (reset) {
                setLogs(response.data.logs);
                setOffset(LIMIT);
            } else {
                setLogs(prev => [...prev, ...response.data.logs]);
                setOffset(prev => prev + LIMIT);
            }
            setTotal(response.data.total);
            setHasLoaded(true);
        } catch (error) {
            console.error("Error loading logs:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (column) => {
        const newDirection = sortColumn === column && sortDirection === 'desc' ? 'asc' : 'desc';
        setSortColumn(column);
        setSortDirection(newDirection);
        // We need to reload logs with new sort, but state update is async.
        // We can pass the new values directly to loadLogs or use useEffect.
        // Using a direct call with explicit params would require refactoring loadLogs to accept overrides.
        // Instead, let's use useEffect to trigger reload when sort changes.
    };

    // Trigger reload when sort changes, but only if we have already loaded data
    React.useEffect(() => {
        if (hasLoaded) {
            loadLogs(true);
        }
    }, [sortColumn, sortDirection]);

    const handleSearch = (e) => {
        if (e) e.preventDefault();
        loadLogs(true);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSearch();
        }
    };

    const handleLoadMore = () => {
        loadLogs(false);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString();
    };

    const handleClearFilters = () => {
        setFilters({
            dateFrom: '',
            dateTo: '',
            ipAddress: '',
            referrer: '',
            params: '',
            uid: '',
            search: ''
        });
    };

    return (
        <div className="space-y-4">
            {/* Filters */}
            <div className="bg-white p-4 rounded-md shadow-sm border border-gray-200" onKeyDown={handleKeyDown}>
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-gray-900">Filter Logs</h3>
                    <button
                        type="button"
                        onClick={handleClearFilters}
                        className="text-sm text-gray-500 hover:text-gray-700 underline"
                    >
                        Clear Filters
                    </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="col-span-1 md:col-span-2">
                        <label className="block text-xs font-medium text-gray-700">General Search</label>
                        <input
                            type="text"
                            name="search"
                            value={filters.search}
                            onChange={handleFilterChange}
                            placeholder="Search all fields..."
                            className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700">Date From</label>
                        <input
                            type="datetime-local"
                            name="dateFrom"
                            value={filters.dateFrom}
                            onChange={handleFilterChange}
                            className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700">Date To</label>
                        <input
                            type="datetime-local"
                            name="dateTo"
                            value={filters.dateTo}
                            onChange={handleFilterChange}
                            className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700">IP Address</label>
                        <input
                            type="text"
                            name="ipAddress"
                            value={filters.ipAddress}
                            onChange={handleFilterChange}
                            placeholder="127.0.0.1"
                            className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700">Referrer</label>
                        <input
                            type="text"
                            name="referrer"
                            value={filters.referrer}
                            onChange={handleFilterChange}
                            placeholder="google.com"
                            className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700">Params</label>
                        <input
                            type="text"
                            name="params"
                            value={filters.params}
                            onChange={handleFilterChange}
                            placeholder="utm_source=..."
                            className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700">UID (Cookie)</label>
                        <input
                            type="text"
                            name="uid"
                            value={filters.uid}
                            onChange={handleFilterChange}
                            className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        />
                    </div>
                    <div className="md:col-span-4 flex justify-end mt-2">
                        <button
                            type="button"
                            onClick={handleSearch}
                            disabled={loading}
                            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                        >
                            {loading ? 'Searching...' : 'Search Logs'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Results */}
            {!hasLoaded ? (
                <div className="text-center py-10 text-gray-500 bg-white rounded-md border border-gray-200 shadow-sm">
                    <p>Enter filters and click Search to view logs.</p>
                </div>
            ) : (
                <div className="flex flex-col">
                    <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                        <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                            <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-100">
                                        <tr>
                                            <th scope="col" className="px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-200" onClick={() => handleSort('created_at')}>
                                                Date {sortColumn === 'created_at' && (sortDirection === 'asc' ? '↑' : '↓')}
                                            </th>
                                            <th scope="col" className="px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-200" onClick={() => handleSort('ipaddr')}>
                                                IP {sortColumn === 'ipaddr' && (sortDirection === 'asc' ? '↑' : '↓')}
                                            </th>
                                            <th scope="col" className="px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-200" onClick={() => handleSort('user_id')}>
                                                User {sortColumn === 'user_id' && (sortDirection === 'asc' ? '↑' : '↓')}
                                            </th>
                                            <th scope="col" className="px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-200" onClick={() => handleSort('contact_tid')}>
                                                Contact {sortColumn === 'contact_tid' && (sortDirection === 'asc' ? '↑' : '↓')}
                                            </th>
                                            <th scope="col" className="px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-200" onClick={() => handleSort('referrer')}>
                                                Referrer {sortColumn === 'referrer' && (sortDirection === 'asc' ? '↑' : '↓')}
                                            </th>
                                            <th scope="col" className="px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-200" onClick={() => handleSort('params')}>
                                                Params {sortColumn === 'params' && (sortDirection === 'asc' ? '↑' : '↓')}
                                            </th>
                                            <th scope="col" className="px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-200" onClick={() => handleSort('cookie')}>
                                                Cookie {sortColumn === 'cookie' && (sortDirection === 'asc' ? '↑' : '↓')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {logs.length > 0 ? (
                                            logs.map((log, idx) => (
                                                <tr key={log.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                    <td className="px-3 py-1 whitespace-nowrap text-xs text-gray-500">{formatDate(log.created_at)}</td>
                                                    <td className="px-3 py-1 whitespace-nowrap text-xs text-gray-900">{log.ipaddr}</td>
                                                    <td className="px-3 py-1 whitespace-nowrap text-xs text-gray-500">{log.user_id || '-'}</td>
                                                    <td className="px-3 py-1 whitespace-nowrap text-xs text-gray-500">
                                                        {log.contact_tid ? (
                                                            <span title={log.title || 'Unknown'}>
                                                                {log.title ? `${log.title} (${log.contact_tid})` : log.contact_tid}
                                                            </span>
                                                        ) : '-'}
                                                    </td>
                                                    <td className="px-3 py-1 text-xs text-gray-500 max-w-xs truncate" title={log.referrer}>{log.referrer}</td>
                                                    <td className="px-3 py-1 text-xs text-gray-500 max-w-xs truncate" title={log.params}>{log.params}</td>
                                                    <td className="px-3 py-1 text-xs text-gray-500 max-w-xs truncate" title={log.cookie}>{log.cookie}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">No logs found matching your criteria.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Pagination / Load More */}
                    {logs.length < total && (
                        <div className="mt-4 text-center">
                            <button
                                type="button"
                                onClick={handleLoadMore}
                                disabled={loading}
                                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                            >
                                {loading ? 'Loading...' : `Load More (${logs.length} of ${total})`}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default WebpageLogs;
